/**
 * Log Viewer Component
 * Display and download runtime logs
 */

import React, { useState, useEffect, useRef } from 'react';
import { Download, Terminal, Trash2, Copy, CheckCircle } from 'lucide-react';
import { useProcessingStore } from '../store/useProcessingStore';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  step?: string;
}

export const LogViewer: React.FC = () => {
  const { state, processId } = useProcessingStore();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [copied, setCopied] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Add log entry
  const addLog = (level: LogEntry['level'], message: string, step?: string) => {
    const timestamp = new Date().toLocaleTimeString('zh-CN', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    setLogs(prev => [...prev, { timestamp, level, message, step }]);
  };

  // Track processing state changes
  useEffect(() => {
    if (!state) return;

    const stepName = state.currentStep;
    const progress = state.progress;

    if (stepName === 'idle') return;

    if (stepName === 'error') {
      addLog('error', state.error || '处理失败', stepName);
    } else if (stepName === 'completed') {
      addLog('success', '所有处理步骤已完成', stepName);
    } else {
      addLog('info', `当前步骤: ${stepName} (${progress}%)`, stepName);
    }

    // Add status messages
    if (state.statusMessages && state.statusMessages.length > 0) {
      const lastMessage = state.statusMessages[state.statusMessages.length - 1];
      if (lastMessage) {
        addLog('info', lastMessage, stepName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.currentStep, state?.progress, state?.statusMessages?.length]);

  // Track process start
  useEffect(() => {
    if (processId) {
      addLog('info', `开始处理 PDF，Process ID: ${processId}`, 'start');
    }
  }, [processId]);

  // Auto scroll to bottom
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Download logs as text file
  const handleDownload = () => {
    const logText = logs.map(log =>
      `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.step ? `[${log.step}] ` : ''}${log.message}`
    ).join('\n');

    const blob = new Blob([logText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `processing-log-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    addLog('info', '日志已下载', 'system');
  };

  // Copy logs to clipboard
  const handleCopy = () => {
    const logText = logs.map(log =>
      `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.step ? `[${log.step}] ` : ''}${log.message}`
    ).join('\n');

    navigator.clipboard.writeText(logText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    addLog('info', '日志已复制到剪贴板', 'system');
  };

  // Clear logs
  const handleClear = () => {
    setLogs([]);
    addLog('info', '日志已清空', 'system');
  };

  // Get log color based on level
  const getLogColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return 'text-red-400';
      case 'warn':
        return 'text-yellow-400';
      case 'success':
        return 'text-green-400';
      default:
        return 'text-blue-400';
    }
  };

  // Get log icon
  const getLogIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return '✖';
      case 'warn':
        return '⚠';
      case 'success':
        return '✔';
      default:
        return '→';
    }
  };

  if (logs.length === 0) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Terminal className="w-5 h-5" />
                <h3 className="text-lg font-semibold">运行日志</h3>
                <span className="text-xs text-gray-400">(0 条)</span>
              </div>
            </div>
          </div>

          {/* Empty State */}
          <div className="bg-gray-950 text-gray-100 p-8 text-center">
            <Terminal className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-sm">
              暂无日志记录
            </p>
            <p className="text-gray-500 text-xs mt-2">
              上传 PDF 并开始处理后，日志将在此处显示
            </p>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 p-3 text-xs text-gray-600 flex items-center justify-between">
            <div>
              日志级别:
              <span className="ml-2 text-blue-600">INFO</span>,
              <span className="ml-1 text-yellow-600">WARN</span>,
              <span className="ml-1 text-red-600">ERROR</span>,
              <span className="ml-1 text-green-600">SUCCESS</span>
            </div>
            <div>
              Process ID: {processId || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Terminal className="w-5 h-5" />
              <h3 className="text-lg font-semibold">运行日志</h3>
              <span className="text-xs text-gray-400">({logs.length} 条)</span>
            </div>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  className="rounded text-blue-600"
                />
                <span>自动滚动</span>
              </label>
              <button
                onClick={handleCopy}
                className="p-2 hover:bg-gray-700 rounded transition-colors"
                title="复制日志"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-700 rounded transition-colors"
                title="下载日志"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleClear}
                className="p-2 hover:bg-gray-700 rounded transition-colors"
                title="清空日志"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Log Content */}
        <div
          ref={logContainerRef}
          className="bg-gray-950 text-gray-100 p-4 font-mono text-sm max-h-96 overflow-y-auto"
        >
          {logs.map((log, index) => (
            <div
              key={index}
              className="mb-1 hover:bg-gray-900 px-2 py-1 rounded transition-colors"
            >
              <span className="text-gray-500">[{log.timestamp}]</span>
              {' '}
              <span className={getLogColor(log.level)}>
                {getLogIcon(log.level)} [{log.level.toUpperCase()}]
              </span>
              {log.step && (
                <>
                  {' '}
                  <span className="text-purple-400">[{log.step}]</span>
                </>
              )}
              {' '}
              <span className="text-gray-300">{log.message}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-3 text-xs text-gray-600 flex items-center justify-between">
          <div>
            日志级别:
            <span className="ml-2 text-blue-600">INFO</span>,
            <span className="ml-1 text-yellow-600">WARN</span>,
            <span className="ml-1 text-red-600">ERROR</span>,
            <span className="ml-1 text-green-600">SUCCESS</span>
          </div>
          <div>
            Process ID: {processId || 'N/A'}
          </div>
        </div>
      </div>
    </div>
  );
};
